// run-metrics-header — 4-tile KPI grid above the storyline.
// Aggregates tokens, cost, duration, and triggered-by from live SSE state
// and run REST data. MiniCards are display-only (interactive=false).

import { useTranslations } from "use-intl";
import { Coins, Clock, Zap, User, PlayCircle, RotateCcw } from "lucide-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { MiniCard } from "@/components/ui/mini-card/mini-card";
import { StatusChip } from "@/components/ui/status-chip/status-chip";
import RunStatusBadge from "@/features/workflows/components/RunStatusBadge";
import { formatUserName } from "@/utils/format-user-name";
import {
  useResumeWorkflowRun,
  useRetryWorkflowRun,
} from "@/hooks/api/mutations/workflows.mutations";
import type { WorkflowRun, RunEvent, RunStatus } from "@/types/workflow.types";
import type { MergedStep } from "./use-run-detail-state";

// A6 Phase 03 marks structurally-bad runs with this slug prefix on
// error_message. Retry handler refuses these BE-side; UI surfaces the
// reason via disabled button + title tooltip so users see WHY before
// pressing.
const POISON_PILL_PREFIX = "poison_pill:";
// A6 Phase 01 cost-gate hard-block sets this exact slug on error_message.
// Retry from the run-detail page is hidden (different remediation: top up
// budget); the cost banner from A5 owns the budget UX.
const COST_BLOCKED_MESSAGE = "cost_budget_exceeded";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDuration(ms?: number): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

function formatCost(usd?: number | null): string {
  if (usd == null) return "—";
  if (usd < 0.001) return "<$0.001";
  return `$${usd.toFixed(4)}`;
}

function formatTokens(n?: number | null): string {
  if (n == null) return "—";
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function normalizedEventType(eventType: string): string {
  return eventType.replace(/\./g, "_");
}

function metadataNumber(metadata: Record<string, unknown> | undefined, key: string): number | null {
  const value = metadata?.[key];
  return typeof value === "number" ? value : null;
}

// Derive live duration from start time + now for active runs.
function liveDurationMs(startedAt?: string, runStatus?: RunStatus | null): number | undefined {
  const isActive =
    runStatus === "running" ||
    runStatus === "paused_checkpoint" ||
    runStatus === "paused_manual";
  if (!startedAt) return undefined;
  if (isActive) return Date.now() - new Date(startedAt).getTime();
  return undefined;
}

// ── Aggregate from events ─────────────────────────────────────────────────────

interface Aggregated {
  totalTokens: number | null;
  totalCostUsd: number | null;
  iterCount: number;
  checkpointsRequested: number;
}

function aggregateEvents(events: RunEvent[]): Aggregated {
  let tokens: number | null = null;
  let cost: number | null = null;
  let iters = 0;
  let checkpoints = 0;

  for (const ev of events) {
    const eventType = normalizedEventType(ev.eventType);
    const d = ev.payload as Record<string, unknown>;
    if (eventType === "step_completed") {
      const t = (d.totalTokens ?? d.total_tokens) as number | undefined;
      const c = (d.costUsd ?? d.cost_usd) as number | undefined;
      if (t != null) tokens = (tokens ?? 0) + t;
      if (c != null) cost = (cost ?? 0) + c;
    }
    if (eventType === "llm_call_complete") {
      const t = (d.totalTokens ?? d.total_tokens) as number | undefined;
      const c = (d.costUsd ?? d.cost_usd) as number | undefined;
      if (t != null) tokens = (tokens ?? 0) + t;
      if (c != null) cost = (cost ?? 0) + c;
    }
    if (eventType === "cost_tick") {
      const c = (d.costUsd ?? d.cost_usd) as number | undefined;
      if (c != null) cost = c; // tick replaces accumulated total
    }
    if (eventType === "agent_tool_call") {
      iters += 1;
    }
    if (eventType === "checkpoint_created") {
      checkpoints += 1;
    }
  }

  return { totalTokens: tokens, totalCostUsd: cost, iterCount: iters, checkpointsRequested: checkpoints };
}

function aggregateSteps(steps: MergedStep[]): Pick<Aggregated, "totalTokens" | "totalCostUsd" | "iterCount"> {
  let tokens: number | null = null;
  let cost: number | null = null;
  let iters = 0;

  for (const step of steps) {
    const inputTokens = metadataNumber(step.metadata, "tokens_input");
    const outputTokens = metadataNumber(step.metadata, "tokens_output");
    const stepTokens =
      inputTokens != null || outputTokens != null
        ? (inputTokens ?? 0) + (outputTokens ?? 0)
        : null;
    const stepCost = step.costUsd ?? metadataNumber(step.metadata, "cost_usd");
    if (stepTokens != null) tokens = (tokens ?? 0) + stepTokens;
    if (stepCost != null) cost = (cost ?? 0) + stepCost;
    if (step.iterCount != null) iters += step.iterCount;
  }

  return { totalTokens: tokens, totalCostUsd: cost, iterCount: iters };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface RunMetricsHeaderProps {
  run: WorkflowRun;
  allEvents: RunEvent[];
  runSteps?: MergedStep[];
  runStatus: RunStatus | null;
  showSummaryRow?: boolean;
}

export function RunMetricsHeader({
  run,
  allEvents,
  runSteps = [],
  runStatus,
  showSummaryRow = true,
}: RunMetricsHeaderProps) {
  const t = useTranslations("workflows");
  const agg = aggregateEvents(allEvents);
  const stepAgg = aggregateSteps(runSteps);
  const totalTokens = agg.totalTokens ?? stepAgg.totalTokens;
  const totalCostUsd = agg.totalCostUsd ?? stepAgg.totalCostUsd;
  const iterCount = agg.iterCount || stepAgg.iterCount;

  const liveMs = liveDurationMs(run.startedAt, runStatus);
  const durationMs = liveMs ?? run.durationMs;

  const triggeredByLabel = run.triggeredByUserObject
    ? formatUserName(run.triggeredByUserObject, { fallback: t("meta.unknown_user") })
    : run.triggeredBy;

  const displayStatus = runStatus ?? run.status;

  // Action visibility flags. Resume targets manual-pause only — checkpoint
  // pause uses the approval flow on the checkpoint alert. Retry targets
  // failed/cancelled non-poison runs; budget-blocked runs hide the
  // button entirely so user is routed to the cost banner instead.
  const navigate = useNavigate();
  const resumeMut = useResumeWorkflowRun();
  const retryMut = useRetryWorkflowRun();
  const onRetryClick = () => {
    retryMut.mutate(run.id, {
      onSuccess: (resp) => {
        // BE returns the NEW run id (not the source). Auto-navigate so
        // the user lands on the fresh run's detail page rather than
        // staring at the still-failed source page; without this they
        // have to discover the new run via the runs-list pane.
        const newRunId = resp.data?.id;
        if (newRunId) {
          navigate({
            to: "/workflows/$id/runs/$runId",
            params: { id: run.workflowId, runId: newRunId },
          });
        }
      },
    });
  };
  const isManualPaused = displayStatus === "paused";
  const isFailedOrCancelled =
    displayStatus === "failed" || displayStatus === "cancelled";
  const isPoisonPilled =
    isFailedOrCancelled &&
    (run.errorMessage?.startsWith(POISON_PILL_PREFIX) ?? false);
  const isCostBlocked =
    isFailedOrCancelled && run.errorMessage === COST_BLOCKED_MESSAGE;
  const showActions = isManualPaused || (isFailedOrCancelled && !isCostBlocked);
  const poisonClass = isPoisonPilled
    ? run.errorMessage?.replace(POISON_PILL_PREFIX, "") ?? "unknown"
    : null;

  return (
    <div className="space-y-3">
      {/* Run ID + status row */}
      {showSummaryRow && (
        <div className="flex items-center gap-2 flex-wrap">
          <RunStatusBadge status={displayStatus} />
          <span className="text-[12px] font-mono text-[var(--muted-foreground)]">
            #{run.id.slice(-6).toUpperCase()}
          </span>
          {run.retriedFromRunId && (
            <Link
              to="/workflows/$id/runs/$runId"
              params={{ id: run.workflowId, runId: run.retriedFromRunId }}
              className="text-[12px] text-[var(--muted-foreground)] hover:underline"
            >
              {t("run.retried_from", {
                id: run.retriedFromRunId.slice(-6).toUpperCase(),
              })}
            </Link>
          )}
          {agg.checkpointsRequested > 0 && (
            <StatusChip tone="warning">
              {t("run.metrics.checkpoints_count", { count: agg.checkpointsRequested })}
            </StatusChip>
          )}
          {iterCount > 0 && (
            <StatusChip tone="queued">
              {t("run.metrics.iter_count", { count: iterCount })}
            </StatusChip>
          )}
        </div>
      )}

      {/* Action CTAs — Resume / Retry. Hidden by default; surface only on
          manual-paused or recoverable-failed runs. */}
      {showActions && (
        <div className="flex items-center gap-2 flex-wrap">
          {isManualPaused && (
            <Button
              variant="primary"
              size="sm"
              startIcon={<PlayCircle size={14} aria-hidden />}
              onClick={() => resumeMut.mutate(run.id)}
              isLoading={resumeMut.isPending}
              loadingText={t("runActions.resume")}
            >
              {t("runActions.resume")}
            </Button>
          )}
          {isFailedOrCancelled && (
            <Button
              variant="outline"
              size="sm"
              startIcon={<RotateCcw size={14} aria-hidden />}
              onClick={onRetryClick}
              isLoading={retryMut.isPending}
              loadingText={t("runActions.retry")}
              disabled={isPoisonPilled || retryMut.isPending}
              title={
                isPoisonPilled
                  ? t("runActions.retry_blocked_poison", {
                      class: poisonClass ?? "unknown",
                    })
                  : undefined
              }
            >
              {t("runActions.retry")}
            </Button>
          )}
        </div>
      )}

      {/* 4-tile KPI grid */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <MiniCard
          head={{ icon: <Clock size={12} aria-hidden />, label: t("run.metrics.duration") }}
          title={formatDuration(durationMs)}
          foot={run.startedAt ? new Date(run.startedAt).toLocaleTimeString() : undefined}
        />
        <MiniCard
          head={{ icon: <Zap size={12} aria-hidden />, label: t("run.metrics.tokens") }}
          title={formatTokens(totalTokens)}
          foot={t("run.metrics.tokens_live_hint")}
        />
        <MiniCard
          head={{ icon: <Coins size={12} aria-hidden />, label: t("run.metrics.cost") }}
          title={formatCost(totalCostUsd)}
          foot={t("run.metrics.cost_live_hint")}
        />
        <MiniCard
          head={{ icon: <User size={12} aria-hidden />, label: t("run.metrics.triggered_by") }}
          title={triggeredByLabel}
        />
      </div>
    </div>
  );
}
